import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Loader2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LocationPicker from "@/components/LocationPicker";
import PhotoReorder from "@/components/PhotoReorder";
import { api, errText } from "@/lib/api";
import { trackEvent } from "@/lib/gtag";

const AMENITIES = ["Lift", "Power backup", "Car parking", "Security", "Gym", "Swimming pool", "Wi-Fi", "Meals included", "Water supply", "Park"];

const Section = ({ title, subtitle, children }) => (
  <section className="rounded-2xl border border-border p-6 md:p-8">
    <h2 className="font-display text-lg">{title}</h2>
    {subtitle && <p className="mt-1 text-[13px] text-muted-foreground">{subtitle}</p>}
    <div className="mt-6 space-y-5">{children}</div>
  </section>
);

export default function PostProperty() {
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", category: "buy", listing_type: "sell", price: "",
    bedrooms: "", bathrooms: "", area_sqft: "", furnishing: "unfurnished", property_kind: "Apartment",
    city: "", locality: "", address: "", contact_name: "", contact_phone: "", amenities: [],
  });
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);

  const locate = async () => {
    if (!form.city.trim()) return;
    setLocating(true);
    try {
      const q = [form.address, form.locality, form.city, "India"].filter(Boolean).join(", ");
      const { data } = await api.get("/geocode", { params: { q } });
      setCoords([data.lat, data.lng]);
      toast.success("Pin placed — drag it to fine-tune");
    } catch {
      toast.error("Could not find that address, drop the pin manually");
    } finally {
      setLocating(false);
    }
  };


  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!editId) return;
    api
      .get(`/properties/${editId}`)
      .then(({ data }) => {
        const p = data.property;
        setForm({
          title: p.title || "", description: p.description || "", category: p.category || "buy",
          listing_type: p.listing_type || "sell", price: p.price ?? "", bedrooms: p.bedrooms ?? "",
          bathrooms: p.bathrooms ?? "", area_sqft: p.area_sqft ?? "",
          furnishing: p.furnishing || "unfurnished", property_kind: p.property_kind || "Apartment",
          city: p.city || "", locality: p.locality || "", address: p.address || "",
          contact_name: p.contact_name || "", contact_phone: p.contact_phone || "",
          amenities: p.amenities || [],
        });
        setImages(p.images || []);
        if (p.lat && p.lng) setCoords([p.lat, p.lng]);
      })
      .catch((err) => toast.error(errText(err)));
  }, [editId]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/upload?watermark=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.file_id]);
      } catch (err) {
        toast.error(errText(err));
      }
    }
    setUploading(false);
  };

  const toggleAmenity = (a) =>    setForm((f) => ({
      ...f,
      amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a],
    }));

  const submit = async (e) => {    e.preventDefault();
    if (!form.title.trim() || !form.city.trim()) {
      toast.error("Title and city are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price || 0),
        bedrooms: form.bedrooms ? Number(form.bedrooms) : null,
        bathrooms: form.bathrooms ? Number(form.bathrooms) : null,
        area_sqft: form.area_sqft ? Number(form.area_sqft) : null,
        lat: coords ? coords[0] : null,
        lng: coords ? coords[1] : null,
        images,
      };
      const { data } = editId
        ? await api.put(`/properties/${editId}`, payload)
        : await api.post("/properties", payload);
      toast.success(editId ? "Listing updated" : "Property published");
      if (!editId) trackEvent("property_listed", { event_category: "listing", city: form.city });
      navigate(`/property/${data.property_id}`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-[900px] px-5 md:px-10 py-10">
      <h1 className="font-display text-3xl sm:text-4xl">{editId ? "Edit your listing" : "List your property"}</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        {editId
          ? "Update the details or photos — changes go live straight away."
          : "It only takes a few minutes. Add real photographs — verified listings get 4x more enquiries."}
      </p>

      <form onSubmit={submit} className="mt-9 space-y-6" data-testid="post-property-form">
        <Section title="The basics" subtitle="Tell buyers what makes this place special.">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Listing title</Label>
            <Input value={form.title} onChange={set("title")} placeholder="3 BHK apartment in Powai with lake view" className="h-12 rounded-lg" data-testid="post-title-input" />
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="h-12 rounded-lg" data-testid="post-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Listing type</Label>
              <Select value={form.listing_type} onValueChange={(v) => setForm({ ...form, listing_type: v })}>
                <SelectTrigger className="h-12 rounded-lg" data-testid="post-listing-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sell">Sell</SelectItem>
                  <SelectItem value="rent">Rent</SelectItem>
                  <SelectItem value="pg">PG / monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Property kind</Label>
              <Input value={form.property_kind} onChange={set("property_kind")} className="h-12 rounded-lg" data-testid="post-kind-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Description</Label>
            <Textarea value={form.description} onChange={set("description")} rows={5} className="rounded-lg" data-testid="post-description-input" />
          </div>
        </Section>

        <Section title="Pricing & size">
          <div className="grid gap-5 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Price ₹</Label>
              <Input type="number" value={form.price} onChange={set("price")} className="h-12 rounded-lg" data-testid="post-price-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Bedrooms</Label>
              <Input type="number" value={form.bedrooms} onChange={set("bedrooms")} className="h-12 rounded-lg" data-testid="post-bedrooms-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Bathrooms</Label>
              <Input type="number" value={form.bathrooms} onChange={set("bathrooms")} className="h-12 rounded-lg" data-testid="post-bathrooms-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Area (sqft)</Label>
              <Input type="number" value={form.area_sqft} onChange={set("area_sqft")} className="h-12 rounded-lg" data-testid="post-area-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Furnishing</Label>
            <Select value={form.furnishing} onValueChange={(v) => setForm({ ...form, furnishing: v })}>
              <SelectTrigger className="h-12 rounded-lg sm:w-64" data-testid="post-furnishing-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unfurnished">Unfurnished</SelectItem>
                <SelectItem value="semi-furnished">Semi furnished</SelectItem>
                <SelectItem value="fully-furnished">Fully furnished</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Section>

        <Section title="Location & contact">
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">City</Label>
              <Input value={form.city} onChange={set("city")} className="h-12 rounded-lg" data-testid="post-city-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Locality</Label>
              <Input value={form.locality} onChange={set("locality")} className="h-12 rounded-lg" data-testid="post-locality-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Full address</Label>
            <Input value={form.address} onChange={set("address")} className="h-12 rounded-lg" data-testid="post-address-input" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold">Pin on map</Label>
              <button
                type="button"
                onClick={locate}
                disabled={locating || !form.city.trim()}
                className="text-xs font-bold underline disabled:opacity-40"
                data-testid="post-locate-btn"
              >
                {locating ? "Locating…" : "Auto-locate from address"}
              </button>
            </div>
            <LocationPicker value={coords} onChange={setCoords} />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Contact name</Label>
              <Input value={form.contact_name} onChange={set("contact_name")} className="h-12 rounded-lg" data-testid="post-contact-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Contact phone</Label>
              <Input value={form.contact_phone} onChange={set("contact_phone")} inputMode="tel" placeholder="10-digit mobile" className="h-12 rounded-lg" data-testid="post-contact-phone-input" />
              <p className="text-[11px] text-muted-foreground">Buyers will see this number on your listing.</p>
            </div>
          </div>
        </Section>

        <Section title="What this place offers">
          <div className="flex flex-wrap gap-2.5">
            {AMENITIES.map((a) => {
              const on = form.amenities.includes(a);
              return (
                <button
                  type="button"
                  key={a}
                  onClick={() => toggleAmenity(a)}
                  className={`flex items-center gap-2 rounded-full border px-4 py-2.5 text-[13px] font-semibold transition-colors duration-200 ${
                    on ? "border-foreground bg-muted" : "border-border hover:border-foreground"
                  }`}
                  data-testid={`amenity-${a.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {on && <Check className="h-3.5 w-3.5" />} {a}
                </button>
              );
            })}
          </div>
        </Section>

        <Section title="Add photos" subtitle="JPG, PNG or WEBP up to 8MB each.">
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-input p-7 hover:border-foreground transition-colors duration-200">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : <Upload className="h-5 w-5 text-primary" />}
            <span className="text-sm text-muted-foreground">{uploading ? "Uploading…" : "Click to upload images"}</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="post-image-upload-input" />
          </label>
          {images.length > 0 && (
            <div data-testid="post-image-previews">
              <PhotoReorder
                images={images}
                onChange={setImages}
                onRemove={(id) => setImages(images.filter((x) => x !== id))}
                testidPrefix="post-photo"
              />
            </div>
          )}
        </Section>

        <div className="flex flex-wrap items-center gap-4">
          <Button type="submit" disabled={busy} className="h-13 rounded-lg px-8 py-4 text-[15px] font-bold hover:brightness-95" data-testid="post-property-submit-btn">
            {busy ? "Saving…" : editId ? "Save changes" : "Publish listing"}
          </Button>
        </div>
      </form>
    </div>
  );
}
